import type { ExecutionLogEntry, LogLevel } from '../types/tab-data.types.js';

/**
 * Canonical field paths on ExecutionLogEntry, confirmed against real SignalR
 * sample payloads. EXECUTION_ID lives at rawDetail.executionID (a sibling of
 * eventInfo), not under eventInfo.currentContext.rootExecutionId — that path
 * looks right from the TS type mirror but is not where the backend puts it.
 */
export const FIELD_PATHS = {
  NODE_LABEL: 'sourceNodeLabel',
  NODE_ID: 'sourceNodeId',
  ENTITY_TYPE: 'rawDetail.item.entityType',
  EXECUTION_ID: 'rawDetail.executionID',
  STAGE: 'eventInfo.eventDescription.stage',
  SUB_STAGE: 'eventInfo.eventDescription.subStage',
  MESSAGE: 'eventInfo.eventDescription.message',
  OUTPUT_DATA: 'rawDetail.outputData',
  INPUT_DATA: 'rawDetail.inputData',
  ERROR: 'rawDetail.error',
  LEVEL: 'level',
} as const;

const ENTITY_TYPE_LABELS: Record<string, string> = {
  Process: 'Process',
  ProcessThread: 'Workflow',
  ProcessElement: 'Node',
};

export function getNodeFilterValue(entry: ExecutionLogEntry): string | undefined {
  return entry.sourceNodeLabel ?? entry.sourceNodeId ?? undefined;
}

export function getEntityType(entry: ExecutionLogEntry): string | undefined {
  return (entry.rawDetail as any)?.item?.entityType;
}

export function getEntityTypeLabel(entityType: string | undefined): string | undefined {
  return entityType ? (ENTITY_TYPE_LABELS[entityType] ?? entityType) : undefined;
}

export function getExecutionId(entry: ExecutionLogEntry): string | undefined {
  return (entry.rawDetail as any)?.executionID;
}

export function getStage(entry: ExecutionLogEntry): string | undefined {
  return entry.eventInfo?.eventDescription?.stage ?? (entry.rawDetail as any)?.stage;
}

export function getSubStage(entry: ExecutionLogEntry): string | undefined {
  return entry.eventInfo?.eventDescription?.subStage ?? (entry.rawDetail as any)?.subStage;
}

export function getDescription(entry: ExecutionLogEntry): string | undefined {
  // Falls through to the entry's own top-level `message` last -- real backend broadcasts
  // always carry a fuller message under eventInfo/rawDetail, but client-synthesized entries
  // (e.g. useExecutionSignalR's SignalR-reconnect "catch-up" status line) only ever set
  // `message` directly, with no eventInfo/rawDetail at all. Without this fallback those
  // entries rendered as a description-less blank row (2026-09-24, reported live by Binoy).
  return entry.eventInfo?.eventDescription?.message ?? (entry.rawDetail as any)?.message ?? entry.message;
}

function matchesNodeKey(entry: ExecutionLogEntry, nodeKey: string): boolean {
  const item = (entry.rawDetail as any)?.item;
  if (item?.key != null && String(item.key) === nodeKey) return true;
  if (item?.id != null && String(item.id) === nodeKey) return true;
  return entry.sourceNodeId === nodeKey;
}

// ── Options / filtering / summary ───────────────────────────────────────────

/** Powers every MultiSelectDropdown's getOptions() — computed fresh on demand, not cached. */
export function getUniqueFieldValues<T>(
  entries: T[],
  getValue: (entry: T) => string | undefined | null
): string[] {
  const values = new Set<string>();
  for (const entry of entries) {
    const value = getValue(entry);
    if (value !== undefined && value !== null && value !== '') {
      values.add(value);
    }
  }
  return Array.from(values).sort();
}

export interface LogFilterCriteria {
  nodeIds?: string[];
  entityTypes?: string[];
  executionIds?: string[];
  stages?: string[];
  subStages?: string[];
  levels?: LogLevel[];
}

/** AND across populated criteria fields, OR within each field's array. */
export function filterExecutionLogEntries(
  entries: ExecutionLogEntry[],
  criteria: LogFilterCriteria
): ExecutionLogEntry[] {
  const { nodeIds, entityTypes, executionIds, stages, subStages, levels } = criteria;

  return entries.filter((entry) => {
    if (nodeIds?.length) {
      const value = getNodeFilterValue(entry);
      if (!value || !nodeIds.includes(value)) return false;
    }
    if (entityTypes?.length) {
      const value = getEntityType(entry);
      if (!value || !entityTypes.includes(value)) return false;
    }
    if (executionIds?.length) {
      const value = getExecutionId(entry);
      if (!value || !executionIds.includes(value)) return false;
    }
    if (stages?.length) {
      const value = getStage(entry);
      if (!value || !stages.includes(value)) return false;
    }
    if (subStages?.length) {
      const value = getSubStage(entry);
      if (!value || !subStages.includes(value)) return false;
    }
    if (levels?.length && !levels.includes(entry.level)) return false;
    return true;
  });
}

export interface LogSummary {
  total: number;
  byLevel: Record<LogLevel, number>;
  byStage: Record<string, number>;
  byEntityType: Record<string, number>;
  byNode: Record<string, number>;
}

export function summarizeExecutionLogEntries(entries: ExecutionLogEntry[]): LogSummary {
  const byLevel: Record<LogLevel, number> = { debug: 0, info: 0, warn: 0, error: 0 };
  const byStage: Record<string, number> = {};
  const byEntityType: Record<string, number> = {};
  const byNode: Record<string, number> = {};

  for (const entry of entries) {
    byLevel[entry.level] = (byLevel[entry.level] ?? 0) + 1;

    const stage = getStage(entry);
    if (stage) byStage[stage] = (byStage[stage] ?? 0) + 1;

    const entityType = getEntityType(entry);
    if (entityType) byEntityType[entityType] = (byEntityType[entityType] ?? 0) + 1;

    const node = getNodeFilterValue(entry);
    if (node) byNode[node] = (byNode[node] ?? 0) + 1;
  }

  return { total: entries.length, byLevel, byStage, byEntityType, byNode };
}

// ── Node-scoped queries (used by the Nodes tab / node detail panels) ───────
//
// Pure functions taking `entries` explicitly, kept dependency-free (no
// Zustand) so they're unit-testable in isolation. FlowObserverPanelEngine
// exposes nodeKey-only convenience wrappers that supply the live
// execution-logs buffer for callers that don't want to reach into the store
// themselves.

/** "Last exit record" in the prompt maps to stage === 'Completed' — no literal "Exit" value exists in any sample payload. */
export function readOutputDataFromLastExecution(
  entries: ExecutionLogEntry[],
  nodeKey: string
): { outputData: unknown; entry: ExecutionLogEntry } | null {
  const matches = entries.filter((entry) => matchesNodeKey(entry, nodeKey) && getStage(entry) === 'Completed');
  if (matches.length === 0) return null;
  const latest = matches[matches.length - 1];
  return { outputData: (latest.rawDetail as any)?.outputData, entry: latest };
}

/**
 * Config tab's data source: the ConfigResolution event's resolvedConfiguration,
 * with nodeFormValues filtered out (a confirmed verbatim duplicate of
 * resolvedConfiguration.{inputPorts,outputPorts} at another nesting level).
 */
export function readNodeConfig(entries: ExecutionLogEntry[], nodeKey: string): Record<string, unknown> | undefined {
  const matches = entries.filter(
    (entry) => matchesNodeKey(entry, nodeKey) && entry.eventInfo?.eventType?.eventName === 'ConfigResolution'
  );
  if (matches.length === 0) return undefined;
  const latest = matches[matches.length - 1];
  const resolvedConfiguration = (latest.eventInfo?.eventDescription?.metadata as any)?.resolvedConfiguration as
    | Record<string, unknown>
    | undefined;
  if (!resolvedConfiguration) return undefined;
  const { nodeFormValues, ...rest } = resolvedConfiguration;
  return rest;
}

/** "stage=initial"/"stage=exit" in the prompt map to the observed values Initiated/Completed. */
export function readLastKnownData(
  entries: ExecutionLogEntry[],
  nodeKey: string
): { initiated?: ExecutionLogEntry; completed?: ExecutionLogEntry } {
  const nodeEntries = entries.filter((entry) => matchesNodeKey(entry, nodeKey));
  const initiated = [...nodeEntries].reverse().find((entry) => getStage(entry) === 'Initiated');
  const completed = [...nodeEntries].reverse().find((entry) => getStage(entry) === 'Completed');
  return { initiated, completed };
}

// ── Historical-execution adaptation (used by the Executions browser) ───────

/**
 * processThreadExecutionApiClient.getById returns a flat aggregate execution
 * record (status/duration/node counts) — the same shape as the search-list
 * rows — not per-node event data. There is nothing to reconstruct a full
 * node-level ExecutionLogEntry[] grid from, so this produces a single
 * synthetic summary entry rather than fabricating per-node rows that don't
 * exist in the response. The backend entity has no status-name string (only
 * an ExecutionStatusID FK), so status is derived from stoppedAt/errorMessage/
 * failedNodes instead of read off a field that doesn't exist.
 */
export interface HistoricalExecutionSummary {
  processThreadExecutionId?: number;
  processExecutionId?: number;
  processThreadId?: number;
  startedAt?: string;
  stoppedAt?: string;
  duration?: number;
  totalNodes?: number;
  completedNodes?: number;
  failedNodes?: number;
  skippedNodes?: number;
  errorMessage?: string;
}

export function deriveHistoricalStatusLabel(summary: HistoricalExecutionSummary): 'Running' | 'Failed' | 'Completed' {
  if (!summary.stoppedAt) return 'Running';
  if (summary.errorMessage || (summary.failedNodes ?? 0) > 0) return 'Failed';
  return 'Completed';
}

export function adaptHistoricalExecutionToLogEntries(
  summary: HistoricalExecutionSummary | null | undefined
): ExecutionLogEntry[] {
  if (!summary) return [];

  const statusLabel = deriveHistoricalStatusLabel(summary);
  const level: LogLevel = statusLabel === 'Failed' ? 'error' : 'info';
  const entry: ExecutionLogEntry = {
    id: `historical-${summary.processThreadExecutionId ?? 'unknown'}`,
    timestamp: summary.startedAt ?? new Date(0).toISOString(),
    level,
    sourceNodeLabel: summary.processThreadExecutionId != null ? `Execution ${summary.processThreadExecutionId}` : 'Historical execution',
    message:
      summary.errorMessage ??
      `${statusLabel} — ${summary.completedNodes ?? 0}/${summary.totalNodes ?? 0} nodes completed`,
    status: statusLabel,
    durationMs: summary.duration,
    rawDetail: { historical: true, ...summary },
  };
  return [entry];
}

// ── Pinned-execution-data fetch (contract only) ─────────────────────────────
//
// No backend endpoint exists for this yet — persistence is a cross-team
// dependency not designed here. Callers must treat `null` as "not available"
// rather than a hard failure.

export interface PinnedExecutionSnapshot {
  input: unknown;
  output: unknown;
}

export async function fetchPinnedExecutionSnapshot(nodeKey: string): Promise<PinnedExecutionSnapshot | null> {
  try {
    const response = await fetch(`/api/process-execution/pinned/${encodeURIComponent(nodeKey)}`);
    if (!response.ok) return null;
    const data = await response.json();
    return { input: data?.input ?? null, output: data?.output ?? null };
  } catch {
    return null;
  }
}
